import { $node } from '@milkdown/kit/utils';

type MdNode = {
  type?: string;
  value?: unknown;
  children?: MdNode[];
};

const parseBlockquotesFromHtml = (html: string): string[] => {
  const match = html.match(/<div\s+class=["']grouped-cards["'][^>]*>([\s\S]*?)<\/div>/i);
  const inner = match?.[1] ?? html;
  const lines = inner.split(/\r?\n/);

  const results: string[] = [];
  let buffer: string[] = [];

  const flush = () => {
    if (buffer.length === 0) return;
    const text = buffer.join(' ').trim();
    if (text) results.push(text);
    buffer = [];
  };

  for (const line of lines) {
    const matchQuote = line.match(/^\s*>\s?(.*)$/);
    if (matchQuote) {
      buffer.push(matchQuote[1]);
    } else if (buffer.length) {
      flush();
    }
  }

  flush();
  return results.length > 0 ? results : [''];
};

/**
 * ProseMirror node definition for grouped-cards.
 *
 * A grouped-cards node is a container that holds multiple card nodes.
 * It renders as a visual group in the editor and serializes to:
 * <div class="grouped-cards">
 *   > Card 1
 *   > Card 2
 * </div>
 */
export const groupedCardsNode = $node('grouped_cards', () => {
  console.log('[groupedCardsNode] schema registered');
  return {
    // Content expression: accept both card nodes (styled) and blockquotes (fallback)
    // This allows children to be either:
    // - 'card' nodes (from remarkSingleCard transformation) - preferred, has styling
    // - 'blockquote' nodes (before transformation or standard blockquotes) - fallback
    content: '(card | blockquote)+',

    // This is a block-level node
    group: 'block',

    // Allow it to be selected as a single unit
    selectable: true,

    // Allow this node to be dragged
    draggable: false,

    // Attributes (none for now, but could add layout options later)
    attrs: {},

    // Parse HTML → ProseMirror node
    parseDOM: [
      {
        tag: 'div.grouped-cards',
        priority: 51, // Higher than default div parsing
        getAttrs: (dom) => {
          // Validate that the div has the correct class
          if (typeof dom === 'string') return false;
          const el = dom as HTMLElement;
          return el.classList.contains('grouped-cards') ? {} : false;
        },
      },
    ],

    // Serialize ProseMirror node → HTML (for rendering in editor)
    toDOM: () => {
      return [
        'div',
        {
          class: 'grouped-cards',
          'data-grouped-cards': 'true',
        },
        0, // Content hole (children rendered here)
      ];
    },

    // Parse markdown → ProseMirror (handled by parser plugin)
    parseMarkdown: {
      match: (node) => {
        const mdNode = node as { type?: unknown; value?: unknown };
        console.log(
          '[groupedCardsNode] parseMarkdown match check:',
          mdNode.type,
          mdNode.value?.toString().substring(0, 50)
        );

        if (mdNode.type === 'grouped_cards') {
          console.log('[groupedCardsNode] matched grouped_cards node');
          return true;
        }

        // Fallback: detect raw HTML blocks with the grouped-cards class
        const isHtmlWithGroupedCards =
          mdNode.type === 'html' &&
          typeof mdNode.value === 'string' &&
          mdNode.value.includes('class="grouped-cards"');

        if (isHtmlWithGroupedCards) {
          console.log('[groupedCardsNode] matched HTML node with grouped-cards class');
        }

        return isHtmlWithGroupedCards;
      },
      runner: (state, node, type) => {
        const children = Array.isArray((node as MdNode).children)
          ? ((node as MdNode).children ?? [])
          : [];

        // Look for 'card' type children (created by remarkSingleCard plugin)
        // If remarkSingleCard runs before this, blockquotes will be converted to cards
        let cards = children.filter((child) => (child as MdNode).type === 'card');

        // Fallback: also accept blockquote nodes (for backwards compatibility)
        if (cards.length === 0) {
          cards = children.filter((child) => (child as MdNode).type === 'blockquote');
        }

        // If no cards/blockquotes were already parsed, derive them from the HTML string.
        if (cards.length === 0) {
          const rawValue = (node as { value?: unknown }).value;
          if (typeof rawValue === 'string') {
            const texts = parseBlockquotesFromHtml(rawValue);
            cards = texts.map((text) => ({
              type: 'blockquote', // Will be converted to 'card' by remarkSingleCard on next parse
              children: [
                {
                  type: 'paragraph',
                  children: [{ type: 'text', value: text }],
                },
              ],
            }));
          }
        }

        // As a final fallback, ensure we always create at least one card so the node renders.
        if (cards.length === 0) {
          cards = [
            {
              type: 'blockquote',
              children: [
                {
                  type: 'paragraph',
                  children: [{ type: 'text', value: '' }],
                },
              ],
            },
          ];
        }

        console.log(
          '[groupedCardsNode] runner: cards',
          cards.length,
          'types:',
          cards.map((c) => (c as MdNode).type).join(','),
          'raw:',
          typeof (node as { value?: unknown }).value === 'string'
            ? (node as { value?: unknown }).value
            : ''
        );

        state.openNode(type);
        // @ts-expect-error - mdast nodes are broader than the transformer types
        state.next(cards);
        state.closeNode();
      },
    },

    // Serialize ProseMirror → markdown (handled by serializer plugin)
    toMarkdown: {
      match: (node) => node.type.name === 'grouped_cards',
      runner: (state, node) => {
        // Basic implementation - will be enhanced by serializer plugin
        state.addNode('html', undefined, '<div class="grouped-cards">\n\n');
        state.next(node.content);
        state.addNode('html', undefined, '\n</div>');
      },
    },
  };
});
